<template>
  <section class="flex w-full flex-col gap-5">
    <section class="flex items-end justify-between">
      <span class="text-2xl font-bold text-white">Scheduled Supply Tracker</span>
      <XButton
        @click="resetDate"
        class="bg-[#5B8CFF] text-white"
      >
        <div class="flex items-center gap-2 font-bold">
          Today
          <FontAwesomeIcon icon="redo-alt" />
        </div>
      </XButton>
    </section>
    <section class="grid w-full grid-cols-2 gap-4 mobile:grid-cols-1">
      <Card class="flex w-full flex-col gap-4 p-4">
        <CirculatingSupplyDonutChart
          v-model="selectedDate"
          :series="stableDonutSeries"
          :color-map="colorMap"
        />
        <LivewatchPoweredBy />
      </Card>
      <Card class="flex w-full flex-col gap-4 p-4">
        <CirculatingSupplyAreaChart
          v-model="selectedDate"
          :series="scheduleSupplyTracker?.list"
          :color-map="colorMap"
        />
        <LivewatchPoweredBy />
      </Card>
    </section>
  </section>
</template>

<script setup lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useVModel } from '@vueuse/core'
import type { ColorType } from 'highcharts'
import { computed, ref } from 'vue'
import { getHighchartsColorSet } from 'xangle-charts/src/composables/helper/color-set'
import XButton from 'xangle-ui/src/components/button/XButton.vue'

import type { LivewatchScheduleSupplyCategoryPercent, LivewatchScheduleSupplyDonut } from '~/api/service/livewatch/schedule-supply-donut.service'
import type { LivewatchScheduleSupplyTracker } from '~/api/service/livewatch/schedule-supply-tracker.service'
import CirculatingSupplyAreaChart from '~/components/@pages/livewatch/charts/CirculatingSupplyAreaChart.vue'
import CirculatingSupplyDonutChart from '~/components/@pages/livewatch/charts/CirculatingSupplyDonutChart.vue'

import LivewatchPoweredBy from './labels/LivewatchPoweredBy.vue'

type Props = {
  scheduleSupplyTracker?: LivewatchScheduleSupplyTracker
  scheduleSupplyDonut?: LivewatchScheduleSupplyDonut
  modelValue: number
}

const props = defineProps<Props>()

const selectedDate = useVModel(props, 'modelValue')

// 이전 도넛 차트 데이터를 유지하여 리사이징 방지
const previousDonutSeries = ref<Array<LivewatchScheduleSupplyCategoryPercent>>([])

const stableDonutSeries = computed(() => {
  // 새로운 데이터가 유효하고 비어있지 않은 경우에만 업데이트
  if (props.scheduleSupplyDonut?.list && props.scheduleSupplyDonut.list.length > 0) {
    previousDonutSeries.value = props.scheduleSupplyDonut.list
    return props.scheduleSupplyDonut.list
  }
  // 그렇지 않으면 이전 데이터 유지
  return previousDonutSeries.value
})

// 색상 매핑: 영역 차트 기준 색상을 도넛 차트에도 동일하게 적용
const colorMap = computed<Record<string, ColorType>>(() => {
  if (!props.scheduleSupplyTracker?.list) return {}
  const colorSet = getHighchartsColorSet(props.scheduleSupplyTracker.list.length, true)
  const map: Record<string, ColorType> = {}
  props.scheduleSupplyTracker.list.forEach((series, idx) => {
    map[series.name] = colorSet[idx]
  })
  return map
})

const resetDate = () => {
  selectedDate.value = new Date().getTime()
}

</script>
